import { useLayoutEffect, useRef, useState } from 'react';
import styles from './SegmentedTab.module.css';

/**
 * Segmented Tab (piece 3): a native radio group, one radio per segment
 * visually hidden behind its own styled label, so arrow-key movement, the
 * single-selection rule and the group's announcement come from the platform.
 * Each segment centres its label by flex alignment; a count sits inside the
 * label text; the highlight slides between segments.
 */
export interface Segment {
  value: string;
  /** The full label, a count included, such as "3 Uncategorized". */
  label: string;
}

export interface SegmentedTabProps {
  name: string;
  legend: string;
  segments: readonly Segment[];
  value: string;
  onChange: (value: string) => void;
}

export const segmentedTabDocumentedStates = ['default', 'withCount'] as const;

export function SegmentedTab({ name, legend, segments, value, onChange }: SegmentedTabProps) {
  const track = useRef<HTMLFieldSetElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const el = track.current;
    if (!el) return;
    const measure = () => {
      const active = el.querySelector<HTMLElement>(`[data-segment="${CSS.escape(value)}"]`);
      if (active) setThumb({ left: active.offsetLeft, width: active.offsetWidth });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [value, segments]);

  return (
    <fieldset ref={track} className={styles.track} data-segmented={name}>
      <legend className={styles.legend}>{legend}</legend>
      {thumb ? <span className={styles.thumb} style={{ left: thumb.left, width: thumb.width }} aria-hidden="true" /> : null}
      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <label key={segment.value} className={`${styles.segment} ${active ? styles.active : ''}`} data-segment={segment.value} data-active={active ? 'true' : 'false'}>
            <input type="radio" className={styles.radio} name={name} value={segment.value} checked={active} onChange={() => onChange(segment.value)} />
            {segment.label}
          </label>
        );
      })}
    </fieldset>
  );
}
